import { useEffect, useRef, useState } from 'react'
import { PageHeader } from '../../components/PageHeader'
import { BackgroundTitle } from '../../components/BackgroundTitle'
import { readError } from '../../lib/errors'
import { useT } from '../../i18n'
import { useAuth } from '../../lib/auth'
import { useGatedAction } from '../../lib/useSubscription'
import {
  startCloseEnoughEndless, closeEnoughEndlessState,
  closeEnoughEndlessGuess, closeEnoughEndlessHint, closeEnoughEndlessGiveUp,
  closeEnoughCoopState, closeEnoughCoopGuess,
  type CloseEndlessState, type CloseCoopState,
} from '../../lib/api/wordEndless'
import {
  hostWordTvRoom, joinWordTvRoom, fetchWordTvRoom, startWordTvRoom,
  leaveWordTvRoom, nextWordRound, skipWordSeat, type WordTvSeat,
} from '../../lib/api/wordTv'
import { EndlessSetup, PassDevice, EndlessOutro, RoomLobby, RoundScoreboard, type SetupMode } from './EndlessSetup'
import { HowToOverlay } from './HowToOverlay'

const TITLE = 'CLOSE ENOUGH'
const ACCENT = '#A8A020'
const BORDER = '#D8D844'
const GLASS = 'btn-glass-closeenough'

const HOW_TO = [
  'One secret word a round. Guess any word and see how close it lands in meaning.',
  'Common words only, no proper nouns. Places and names are not in the dictionary.',
  '1 is the word itself. Low numbers are close and show green, high numbers drift to red, and cold means the meaning is far away.',
  'Start with 600 points. Every guess after the first costs 5.',
  'Three hints a round, each one closer than the last. Every hint costs 50 points.',
  'Play solo, pass the phone, or open a private room: everyone hunts the same word and the highest score wins.',
  'In a room, up to 16 people play in parallel on their own phones. Only the host needs a subscription, so guests play free.',
  'You never see the same word twice, in this mode or the daily.',
]

/** Co-op verdict per the owner's bands (2026-08-19). */
function coopBand(score: number): string {
  if (score >= 550) return 'Excellent!'
  if (score >= 500) return 'Great!'
  if (score >= 400) return 'Good'
  if (score >= 300) return 'Poor'
  return 'Nice try'
}

/** Same warmth scale as the daily: green is close, red is far. */
function warmth(rank: number | null): { label: string; className: string } {
  if (rank === null) return { label: 'cold', className: 'bg-[#C62828] text-white' }
  if (rank === 1) return { label: '1', className: 'bg-brand-success text-white' }
  if (rank <= 10) return { label: `${rank}`, className: 'bg-[#2E7D32] text-white' }
  if (rank <= 100) return { label: `${rank}`, className: 'bg-[#0EAE93] text-white' }
  if (rank <= 500) return { label: `${rank}`, className: 'bg-brand-lime text-brand-text' }
  if (rank <= 1500) return { label: `${rank}`, className: 'bg-[#F2A93A] text-white' }
  return { label: `${rank}`, className: 'bg-[#D4603A] text-white' }
}

type Phase = 'setup' | 'lobby' | 'pass' | 'playing' | 'results' | 'room_done'

interface Props {
  onBack: () => void
  onPlayWeekly: () => void
  onOpenPaywall: () => void
  /** Set when arriving with a room code (QR scan, push, Jump back in) —
   *  auto-claims (or re-claims) a seat on mount. */
  resumeCode?: string
  /** Fired when the auto-join is ATTEMPTED, not when it succeeds. */
  onResumeConsumed?: () => void
}

/**
 * CLOSE ENOUGH, endless. Same meaning-rank engine and scoring as the
 * daily, drawn from the pool — the give-up button included. In
 * pass-and-play every player hunts the SAME secret in turn; in a
 * private room every seat hunts it in parallel on their own phone, and
 * nothing but finish-state and scores shows until the round is over.
 */
export function CloseEnoughEndlessScreen({
  onBack, onPlayWeekly, onOpenPaywall, resumeCode, onResumeConsumed,
}: Props) {
  const t = useT()
  const { profile } = useAuth()
  const gate = useGatedAction(onOpenPaywall)
  const [phase, setPhase] = useState<Phase>('setup')
  const [session, setSession] = useState<string | null>(null)
  const [players, setPlayers] = useState(1)
  const [names, setNames] = useState<string[]>([])
  const [player, setPlayer] = useState(0)
  const [state, setState] = useState<CloseEndlessState | null>(null)
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [guessError, setGuessError] = useState<string | null>(null)
  const [lastGuess, setLastGuess] = useState<{ guess: string; rank: number | null; isHint?: boolean } | null>(null)
  const [showHowTo, setShowHowTo] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [setupMode, setSetupMode] = useState<SetupMode>('choose')
  // Room mode. null = solo / pass-and-play.
  const [roomCode, setRoomCode] = useState<string | null>(null)
  // 'coop' = one shared board for the whole table (no hints, shared
  // score); 'party' = every seat races its own board. Solo and
  // pass-and-play never read this.
  const [roomMode, setRoomMode] = useState<'party' | 'coop'>('party')
  // Co-op extras the shared JSX cannot carry: who played each guess,
  // and what the word is worth right now.
  const [coop, setCoop] = useState<CloseCoopState | null>(null)
  const [isHost, setIsHost] = useState(false)
  const [roomSeats, setRoomSeats] = useState<WordTvSeat[]>([])
  const [roomStatus, setRoomStatus] = useState<'lobby' | 'playing' | 'finished' | 'abandoned'>('lobby')
  const [roomRounds, setRoomRounds] = useState(1)
  const [roomRound, setRoomRound] = useState(1)
  const [leaveArmed, setLeaveArmed] = useState(false)

  async function begin(seats: string[]) {
    if (busy) return
    const n = Math.max(1, seats.length)
    setBusy(true); setError(null)
    try {
      const id = await startCloseEnoughEndless(n)
      setSession(id); setPlayers(n); setNames(seats); setPlayer(0)
      setPhase(seats.length > 1 ? 'pass' : 'playing')
    } catch (e) { setError(readError(e)) } finally { setBusy(false) }
  }

  /** The name typed to join a party, if there is one. */
  function typedJoinName(): string {
    try { return localStorage.getItem('jdg_player_name')?.trim() ?? '' } catch { return '' }
  }

  function joinSeatName(): string {
    return typedJoinName() || profile?.display_name?.trim() || ''
  }

  function hostSeatName(): string {
    return profile?.display_name?.trim() || typedJoinName() || ''
  }

  /**
   * Open a private room: create the session on our own account (that is
   * where the All-Access check lives), then mint a code around it.
   * Guests pay nothing — one subscriber opens the table.
   */
  async function hostRoom(seatCount: number, rounds: number, coopRoom = false) {
    if (busy) return
    setBusy(true); setError(null)
    try {
      const id = await startCloseEnoughEndless(seatCount)
      const code = await hostWordTvRoom('close_enough', id, hostSeatName(), rounds,
        coopRoom ? 'coop' : 'party')
      const room = await fetchWordTvRoom(code)
      setSession(id); setPlayers(seatCount); setPlayer(0)
      setNames((room?.seats ?? []).map(s => s.name))
      setRoomCode(code); setIsHost(true)
      setRoomMode(room?.mode ?? (coopRoom ? 'coop' : 'party')); setCoop(null)
      setRoomSeats(room?.seats ?? []); setRoomStatus(room?.status ?? 'lobby')
      setRoomRounds(room?.rounds ?? rounds); setRoomRound(room?.current_round ?? 1)
      setPhase('lobby')
    } catch (e) { setError(readError(e)) } finally { setBusy(false) }
  }

  /** Claim (or re-claim) a seat in a room and play it on this phone. */
  async function joinRoom(code: string) {
    if (busy) return
    setBusy(true); setError(null)
    try {
      const j = await joinWordTvRoom(code, joinSeatName())
      const room = await fetchWordTvRoom(j.code)
      const seats = room?.seats ?? []
      const status = (room?.status ?? 'lobby') as 'lobby' | 'playing' | 'finished' | 'abandoned'
      setSession(j.session); setPlayer(j.player)
      setPlayers(seats.length || 1)
      setNames(seats.map(s => s.name))
      setRoomCode(j.code)
      setIsHost(typeof room?.host_seat === 'number' ? room.host_seat === j.player : false)
      setRoomMode(room?.mode ?? 'party'); setCoop(null)
      setRoomSeats(seats); setRoomStatus(status)
      setRoomRounds(room?.rounds ?? 1); setRoomRound(room?.current_round ?? 1)
      if (status === 'playing') await enterRoomPlay(j.session, j.player, room?.mode ?? 'party')
      else setPhase('lobby')
    } catch (e) { setError(readError(e)) } finally { setBusy(false) }
  }

  /** Map the shared board into the solo-state shape the JSX renders.
   *  Hints and give-up do not exist in co-op, so those legs stay inert. */
  function applyCoop(cs: CloseCoopState) {
    setCoop(cs)
    setState({
      players: cs.players, player: cs.player, guesses: cs.guesses, hints: 0,
      solved: cs.solved, gaveUp: false, score: cs.score, secret: cs.secret,
      list: cs.list.map(g => ({ guess: g.guess, rank: g.rank, isHint: false })),
      scores: [],
    })
  }

  /** Enter (or re-enter) room play; a finished seat goes straight to
   *  the scoreboard instead of replaying its solved word. */
  async function enterRoomPlay(sess: string, seat: number, mode: 'party' | 'coop' = roomMode) {
    // The playing effect loads off the session STATE — keep it in sync
    // when a round flip hands us a fresh session.
    setSession(sess)
    setInput(''); setLastGuess(null); setGuessError(null)
    if (mode === 'coop') {
      const cs = await closeEnoughCoopState(sess, seat)
      applyCoop(cs)
      setPhase(cs.solved ? 'room_done' : 'playing')
      return
    }
    const s = await closeEnoughEndlessState(sess, seat)
    setState(s)
    setPhase(s.solved || s.gaveUp ? 'room_done' : 'playing')
  }

  /** Host only: flips the room to playing (fires the push) and deals in. */
  async function startRoom() {
    if (!roomCode || busy) return
    setBusy(true); setError(null)
    try {
      await startWordTvRoom(roomCode)
      setRoomStatus('playing')
      setPhase('playing')
    } catch (e) { setError(readError(e)) } finally { setBusy(false) }
  }

  /**
   * Leave the room (host leaving abandons it) and reset to the menu.
   * Host closes with a two-tap arm instead of window.confirm — embedded
   * webviews swallow confirm() and the button reads as dead.
   */
  async function leaveRoomToSetup() {
    if (!roomCode) return
    if (isHost && !leaveArmed) {
      setLeaveArmed(true)
      setTimeout(() => setLeaveArmed(false), 3500)
      return
    }
    try { await leaveWordTvRoom(roomCode) } catch { /* best effort */ }
    playAgain()
  }

  /** Host only: deal the next round once the table is done. */
  async function startNextRound() {
    if (!roomCode || busy) return
    setBusy(true); setError(null)
    try {
      const r = await nextWordRound(roomCode)
      setRoomRound(r.current_round)
      await enterRoomPlay(r.session, player)
    } catch (e) { setError(readError(e)) } finally { setBusy(false) }
  }

  /** Host only: skip/unskip a straggler so the table can move on. */
  async function toggleSkip(seat: WordTvSeat) {
    if (!roomCode || !isHost) return
    try {
      await skipWordSeat(roomCode, seat.player, !seat.skipped)
      setRoomSeats(prev => prev.map(x =>
        x.player === seat.player ? { ...x, skipped: !seat.skipped } : x))
    } catch (e) { setError(readError(e)) }
  }


  // Arrived with a code (QR scan, push tap, Jump back in): claim the
  // seat straight away. The ref guards StrictMode's double-effect from
  // burning two seats.
  const autoJoined = useRef(false)
  useEffect(() => {
    if (!resumeCode || autoJoined.current) return
    autoJoined.current = true
    onResumeConsumed?.()
    void joinRoom(resumeCode)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeCode])

  // Room poll while waiting (lobby) or watching others finish
  // (room_done). Realtime is useless here — the endless tables have RLS
  // with zero policies, so a subscription would deliver silence.
  useEffect(() => {
    if (!roomCode || (phase !== 'lobby' && phase !== 'room_done')) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout>
    const tick = async () => {
      try {
        const r = await fetchWordTvRoom(roomCode)
        if (cancelled) return
        if (r) {
          setRoomSeats(r.seats)
          setRoomStatus(r.status)
          setRoomRounds(r.rounds ?? 1)
          setRoomRound(r.current_round ?? 1)
          setNames(r.seats.map(s => s.name))
          setPlayers(r.seats.length || 1)
          // Hand-off support: the server may promote another seat to
          // host when the original host leaves. Trust the payload over
          // local state so the promoted phone grows the host controls.
          if (typeof r.host_seat === 'number') setIsHost(r.host_seat === player)
          setRoomMode(r.mode ?? 'party')
          if (r.status === 'abandoned') {
            setError(t('The host closed this room.'))
            playAgain()
            return
          }
          if (phase === 'lobby' && r.status === 'playing' && !isHost && session) {
            await enterRoomPlay(r.session ?? session, player, r.mode ?? 'party')
            return
          }
          // The host advanced while we sat on the scoreboard: follow
          // the table into the fresh round.
          if (phase === 'room_done' && r.status === 'playing' && r.session && session
              && r.session !== session) {
            await enterRoomPlay(r.session, player, r.mode ?? 'party')
            return
          }
        }
      } catch { /* transient; the next tick retries */ }
      if (!cancelled) timer = setTimeout(tick, 2500)
    }
    void tick()
    return () => { cancelled = true; clearTimeout(timer) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomCode, phase, isHost, session, player])

  // No auto-close, and no host close button. Owner rule (2026-08-15): a
  // host leaves a room, they do not shut it down on everyone else. A room
  // ends when its last seat leaves (abandon_humanless_rooms), when a
  // lobby goes an hour without starting, or never. word_endless_finish is
  // a server-side no-op for the same reason.







  /** Seat label: the typed name in pass-and-play, "You" when solo. */
  function seatName(i: number): string {
    return names[i]?.trim() || (players > 1 ? t('Player {n}', { n: i + 1 }) : t('You'))
  }

  useEffect(() => {
    if (phase !== 'playing' || !session) return
    let cancelled = false
    setState(null); setLastGuess(null); setGuessError(null)
    if (roomMode === 'coop' && roomCode) {
      closeEnoughCoopState(session, player)
        .then(cs => { if (!cancelled) applyCoop(cs) })
        .catch(e => { if (!cancelled) setError(readError(e)) })
    } else {
      closeEnoughEndlessState(session, player)
        .then(s => { if (!cancelled) setState(s) })
        .catch(e => { if (!cancelled) setError(readError(e)) })
    }
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, session, player])

  // Co-op live board: the table's guesses land on every phone as they
  // happen, same cadence as the room poll. Stops once solved.
  useEffect(() => {
    if (phase !== 'playing' || !session || !roomCode || roomMode !== 'coop') return
    if (state?.solved) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout>
    const tick = async () => {
      try {
        const cs = await closeEnoughCoopState(session, player)
        if (cancelled) return
        applyCoop(cs)
        if (cs.solved) return
      } catch { /* transient; the next tick retries */ }
      if (!cancelled) timer = setTimeout(tick, 2500)
    }
    timer = setTimeout(tick, 2500)
    return () => { cancelled = true; clearTimeout(timer) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, session, player, roomCode, roomMode, state?.solved])

  async function guess() {
    const word = input.trim().toLowerCase()
    if (!word || busy || !session || state?.solved) return
    setBusy(true); setGuessError(null)
    try {
      if (roomMode === 'coop' && roomCode) {
        const r = await closeEnoughCoopGuess(session, player, word)
        setInput('')
        if (!r.solved) setLastGuess({ guess: word, rank: r.rank ?? null })
        applyCoop(await closeEnoughCoopState(session, player))
      } else {
        const r = await closeEnoughEndlessGuess(session, player, word)
        setInput('')
        if (!r.repeat || !r.solved) setLastGuess({ guess: word, rank: r.rank ?? null })
        setState(await closeEnoughEndlessState(session, player))
      }
      inputRef.current?.focus()
    } catch (e) { setGuessError(readError(e)) } finally { setBusy(false) }
  }

  async function hint() {
    if (busy || !session || state?.solved) return
    setBusy(true); setGuessError(null)
    try {
      const h = await closeEnoughEndlessHint(session, player)
      setLastGuess({ guess: h.word, rank: h.rank, isHint: true })
      setState(await closeEnoughEndlessState(session, player))
    } catch (e) { setGuessError(readError(e)) } finally { setBusy(false) }
  }

  // Endless only. The Weekly daily has no give-up on purpose: everyone
  // plays the same secret that day, so revealing it would leak the answer
  // to players who haven't finished.
  async function giveUp() {
    if (busy || !session || state?.solved || state?.gaveUp) return
    if (roomMode === 'coop' && roomCode) return
    if (!window.confirm(t('Give up and see the answer? This round scores 0.'))) return
    setBusy(true); setGuessError(null)
    try {
      await closeEnoughEndlessGiveUp(session, player)
      setLastGuess(null)
      setState(await closeEnoughEndlessState(session, player))
    } catch (e) { setGuessError(readError(e)) } finally { setBusy(false) }
  }

  async function advance() {
    if (!session) return
    // In a room everyone else is hunting in parallel on their own
    // phone — go watch the scoreboard instead of passing.
    if (roomCode) { setPhase('room_done'); return }
    if (player + 1 < players) { setPlayer(player + 1); setPhase('pass'); return }
    setBusy(true)
    try {
      setState(await closeEnoughEndlessState(session, player))
      setPhase('results')
    } catch (e) { setError(readError(e)) } finally { setBusy(false) }
  }

  function playAgain() {
    setSession(null); setState(null); setLastGuess(null)
    setPlayer(0)
    setRoomCode(null); setIsHost(false); setRoomSeats([]); setRoomStatus('lobby')
    setRoomMode('party'); setCoop(null)
    setRoomRounds(1); setRoomRound(1); setLeaveArmed(false)
    setSetupMode('choose')
    setPhase('setup')
  }

  const solved = state?.solved ?? false
  const gaveUp = state?.gaveUp ?? false
  const ceActive = roomSeats.filter(s => s.joined && !s.skipped)
  const ceRoundOver = roomStatus === 'finished'
    || (ceActive.length > 0 && ceActive.every(s => s.done === 1))
  // Both end the round and both reveal the secret, so they share the
  // result card; only the wording and the score differ.
  const roundOver = solved || gaveUp

  // Co-op render extras: one shared board, so rows carry who played
  // them and the result cards name the solver and the verdict band.
  const coopMode = roomMode === 'coop' && roomCode !== null
  const coopSolver = coopMode && coop?.solvedBy != null ? seatName(coop.solvedBy) : null
  const coopNames = coopMode && coop
    ? new Map(coop.list.map(g => [g.guess, g.name || seatName(g.by)]))
    : null

  return (
    <div className="screen">
      <BackgroundTitle text={TITLE} color="#C9C930" />
      <PageHeader
        title={TITLE}
        subtitle={players > 1 && phase !== 'setup' && phase !== 'results'
          ? t('Player {n} of {total}', { n: player + 1, total: players })
          : t('Guess your way to the secret word by meaning')}
        onBack={phase === 'setup' && setupMode !== 'choose'
          ? () => setSetupMode('choose')
          : onBack}
      />

      <div className="flex-1 overflow-y-auto px-5 pb-8 relative z-10 glass-panes">
        <button
          type="button"
          onClick={() => setShowHowTo(true)}
          className="w-full mb-3 py-2.5 rounded-full border-2 border-brand-border bg-white
                     text-xs font-semibold uppercase tracking-widest text-brand-muted
                     active:scale-[0.98] transition-transform"
        >
          {t('How to play')}
        </button>

        {/* Give up sits here, at the top of the scroll area, for two
            reasons learned the hard way.

            It was in a fixed bottom bar, which on a phone rides up and
            down with the software keyboard while you are typing a guess.
            Anchored to the content instead, it stays put.

            And it only appears once a guess is in. An escape hatch
            offered before the player has tried anything invites quitting
            a word they have not looked at yet, and it would otherwise be
            the second thing on an empty screen. */}
        {phase === 'playing' && state && !roundOver && !coopMode && state.guesses > 0 && (
          <button
            onClick={() => void giveUp()}
            disabled={busy}
            className={`${GLASS} w-full mb-3 py-2.5 text-xs font-semibold uppercase
                       tracking-widest active:scale-[0.98] transition-transform
                       disabled:opacity-40`}
          >
            {t('Give up and see the answer')}
          </button>
        )}

        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30 mb-3">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}


        {/* The host reads this out; a guest sees which room they are in.
            Kept above the play area so it survives every phase. */}
        {roomCode && phase !== 'setup' && phase !== 'lobby' && (
          <div className="card flex items-center justify-between gap-3 mb-3 rounded-2xl border-2 px-4 py-2.5"
               style={{ borderColor: BORDER }}>
            <span className="text-[10px] font-semibold uppercase tracking-widest text-brand-muted">
              {roomRounds > 1 ? t('Round {n} of {total}', { n: roomRound, total: roomRounds }) : t('Room')}
            </span>
            <span className="font-display font-black text-xl tracking-[0.2em]"
                  style={{ color: ACCENT }}>
              {roomCode}
            </span>
          </div>
        )}

        {phase === 'setup' && (
          <EndlessSetup
            accent={ACCENT} border={BORDER} glassClass={GLASS}
            mode={setupMode} onModeChange={setSetupMode}
            gameImage="/games/close-enough.webp" busy={busy}
            onStart={seats => gate(() => begin(seats))}
            onHostRoom={(n, r, coop) => gate(() => hostRoom(n, r, coop))}
            coopChoice
            onJoinRoom={joinRoom}
            onPlayWeekly={onPlayWeekly}
          />
        )}

        {phase === 'lobby' && roomCode && coopMode && (
          <div className="card p-3 mb-3 rounded-2xl border-2 text-center" style={{ borderColor: BORDER }}>
            <p className="text-xs font-semibold text-brand-text">
              {t('Co-op')} · {t('One shared board. Guess together, no hints. Every wrong guess costs the team 5 points.')}
            </p>
          </div>
        )}

        {phase === 'lobby' && roomCode && (
          <RoomLobby
            code={roomCode} game="close_enough" accent={ACCENT} border={BORDER}
            glassClass={GLASS} isHost={isHost} seats={roomSeats} busy={busy}
            leaveArmed={leaveArmed}
            onStart={() => void startRoom()}
            onLeave={() => void leaveRoomToSetup()}
          />
        )}

        {phase === 'room_done' && (
          <>
            {state?.secret && (
              <div className={`card p-4 mt-1 rounded-2xl text-center border-2 ${state.gaveUp ? '' : 'bg-game-closeenough-light'}`}
                 style={{ borderColor: BORDER, ...(state.gaveUp ? { backgroundColor: '#F4F4F4' } : {}) }}>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-muted">
                  {coopMode && coopSolver
                    ? t('Solved by {name} in {n} team guesses', { name: coopSolver, n: state.guesses })
                    : state.gaveUp ? t('Gave up after {n} guesses', { n: state.guesses }) : t('Solved in {n} guesses', { n: state.guesses })}
                </p>
                <p className="font-display font-bold text-3xl uppercase tracking-wide mt-1"
                   style={{ color: state.gaveUp ? '#6B6B6B' : ACCENT }}>
                  {state.secret}
                </p>
                <p className="font-display font-bold text-lg text-brand-text mt-1">
                  {t('{n} pts', { n: state.score ?? 0 })}
                  {coopMode && state.score != null ? ` · ${t(coopBand(state.score))}` : ''}
                </p>
              </div>
            )}

            <div className="card p-4 mt-3 rounded-2xl text-center border-2 bg-game-closeenough-light"
                 style={{ borderColor: BORDER }}>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-muted">
                {!ceRoundOver ? t('Waiting for everyone to finish…')
                  : roomRound < roomRounds ? t('Round {n} of {total}', { n: roomRound, total: roomRounds })
                  : t('Final standings')}
              </p>
              <div className="mt-2 space-y-1.5">
                {[...roomSeats].filter(s => s.joined)
                  .sort((a, b) => (b.score ?? -1) - (a.score ?? -1))
                  .map((s, i) => (
                    <div key={s.player}
                         className="card flex items-center gap-3 border border-brand-border px-3 py-2">
                      <span className="w-7 text-center font-display font-bold"
                            style={{ color: i === 0 ? ACCENT : '#6B7280' }}>
                        {i + 1}
                      </span>
                      <span className={`flex-1 text-left text-sm font-semibold truncate
                                        ${s.skipped ? 'text-brand-muted line-through' : 'text-brand-text'}`}>
                        {s.name}{s.player === player ? ` · ${t('You')}` : ''}
                      </span>
                      {isHost && !ceRoundOver && s.player !== player && (
                        <button
                          type="button"
                          onClick={() => void toggleSkip(s)}
                          className="text-[10px] font-semibold uppercase tracking-widest
                                     text-brand-muted underline shrink-0"
                        >
                          {s.skipped ? t('Unskip') : t('Skip')}
                        </button>
                      )}
                      <span className="text-xs text-brand-muted">
                        {s.done === 1
                          ? ((s.guesses ?? 0) === 1 ? t('1 guess') : t('{n} guesses', { n: s.guesses ?? 0 }))
                          : '…'}
                      </span>
                      <span className="font-display font-bold text-brand-text">{s.score ?? '—'}</span>
                    </div>
                  ))}
              </div>
            </div>

            {/* The whole game's arithmetic, once the last round is done:
                every round's points per player, ranked. Between rounds
                the seat list above is the running total, which is what
                you want mid-game. */}
            {ceRoundOver && roomRound >= roomRounds && (
              <RoundScoreboard seats={roomSeats} accent={ACCENT} border={BORDER}
                               youPlayer={player} />
            )}

            {/* Leave is ALWAYS available and is the only way out. A host
                cannot close a room other people are sitting in (owner
                rule): the room ends when everyone leaves.

                Next round is shown to the host whenever rounds remain,
                DISABLED until the table is done rather than hidden. It
                used to be hidden, which left a waiting host looking at a
                single "Finish room" button and no way forward — the Skip
                links that unblock it are up in the seat list and easy to
                miss, so the hint names them. */}
            {roomStatus === 'finished' ? (
              <EndlessOutro glassClass={GLASS} accent={ACCENT}
                            onPlayAgain={playAgain} onBackToGames={onBack} />
            ) : (
              <>
                {isHost && roomRound < roomRounds && (
                  <button
                    type="button"
                    disabled={busy || !ceRoundOver}
                    onClick={() => void startNextRound()}
                    className={`${GLASS} w-full mt-5 py-4 text-center font-display font-bold text-xl
                                active:scale-[0.98] transition-transform disabled:opacity-40`}
                  >
                    {busy ? t('Starting…') : t('Next round')}
                  </button>
                )}
                {isHost && roomRound < roomRounds && !ceRoundOver && (
                  <p className="text-center text-brand-muted text-xs mt-2">
                    {t('Everyone has to finish first. Skip anyone who is stuck.')}
                  </p>
                )}
                {!isHost && ceRoundOver && roomRound < roomRounds && (
                  <p className="text-center text-brand-muted text-sm animate-pulse mt-5">
                    {t('Waiting for the host to start the next round…')}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => void leaveRoomToSetup()}
                  className={`${GLASS} w-full mt-3 py-3.5 text-center font-display font-bold text-lg
                              active:scale-[0.98] transition-transform`}
                  style={{ color: ACCENT }}
                >
                  {t('Leave room')}
                </button>
              </>
            )}
          </>
        )}

        {phase === 'pass' && (
          <PassDevice name={seatName(player)} accent={ACCENT} border={BORDER}
                      onReady={() => setPhase('playing')} />
        )}

        {phase === 'playing' && !state && (
          <div className="h-40 rounded-2xl bg-brand-elevated animate-pulse" />
        )}

        {phase === 'playing' && state && (
          <>
            {roundOver ? (
              <>
                <div className={`card p-4 mt-1 rounded-2xl text-center border-2 ${gaveUp ? '' : 'bg-game-closeenough-light'}`}
                 style={{ borderColor: BORDER, ...(gaveUp ? { backgroundColor: '#F4F4F4' } : {}) }}>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-muted">
                    {coopMode && coopSolver
                      ? t('Solved by {name} in {n} team guesses', { name: coopSolver, n: state.guesses })
                      : gaveUp
                      ? (state.guesses === 1
                          ? t('Gave up after 1 guess')
                          : t('Gave up after {n} guesses', { n: state.guesses }))
                      : (state.guesses === 1
                          ? t('Solved in 1 guess')
                          : t('Solved in {n} guesses', { n: state.guesses }))}
                    {state.hints > 0
                      ? (state.hints === 1 ? t(' · 1 hint') : t(' · {n} hints', { n: state.hints }))
                      : ''}
                  </p>
                  <p className="font-display font-bold text-4xl uppercase tracking-wide mt-1"
                     style={{ color: gaveUp ? '#6B6B6B' : ACCENT }}>
                    {state.secret}
                  </p>
                  <p className="font-display font-bold text-xl text-brand-text mt-1">
                    {t('{n} pts', { n: state.score ?? 0 })}
                    {coopMode && state.score != null ? ` · ${t(coopBand(state.score))}` : ''}
                  </p>
                </div>
                <button
                  onClick={() => void advance()}
                  disabled={busy}
                  className="w-full mt-4 py-3.5 rounded-2xl font-display font-bold text-lg
                             bg-brand-text text-white active:scale-[0.98] transition-transform
                             disabled:opacity-40"
                >
                  {roomCode ? t('See standings')
                    : player + 1 < players ? t('Pass the phone') : t('See results')}
                </button>
              </>
            ) : (
              <>
                <div className="flex gap-2 mt-1">
                  <input
                    ref={inputRef}
                    value={input}
                    onChange={e => { setInput(e.target.value); setGuessError(null) }}
                    onKeyDown={e => { if (e.key === 'Enter') void guess() }}
                    placeholder={t('Type a word')}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    className="flex-1 min-w-0 rounded-2xl border-2 border-brand-border bg-white
                               px-4 py-3 text-base outline-hidden focus:border-brand-text"
                  />
                  <button
                    onClick={() => void guess()}
                    disabled={!input.trim() || busy}
                    className="px-5 rounded-2xl font-display font-bold text-brand-text
                               active:scale-[0.98] transition-transform disabled:opacity-40"
                    style={{ backgroundColor: BORDER }}
                  >
                    {t('Guess')}
                  </button>
                </div>
                {!coopMode && <button
                  onClick={() => void hint()}
                  disabled={busy || state.hints >= 3}
                  className="w-full mt-2 py-2.5 rounded-2xl border-2 text-sm font-semibold
                             active:scale-[0.98] transition-transform disabled:opacity-40"
                  style={{ borderColor: BORDER, color: ACCENT }}
                >
                  {state.hints >= 3
                    ? t('No hints left')
                    : t('Hint · 50 pts each · {n} left', { n: 3 - state.hints })}
                </button>}
                {guessError && <p className="text-xs text-brand-danger mt-2">{guessError}</p>}
                {lastGuess && (
                  <div className="flex items-center gap-3 mt-3 rounded-2xl px-4 py-3 border-2 bg-game-closeenough-light"
                       style={{ borderColor: BORDER }}>
                    <span className="flex-1 font-semibold text-brand-text">
                      {lastGuess.guess}
                      {lastGuess.isHint && (
                        <span className="ml-2 text-[10px] font-semibold uppercase tracking-widest text-brand-muted">
                          {t('hint')}
                        </span>
                      )}
                    </span>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${warmth(lastGuess.rank).className}`}>
                      {warmth(lastGuess.rank).label === 'cold' ? t('cold') : warmth(lastGuess.rank).label}
                    </span>
                  </div>
                )}
                <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-muted mt-4 mb-2">
                  {coopMode
                    ? t('{n} team guesses · worth {p} pts right now', { n: state.guesses, p: coop?.poolLeft ?? 600 })
                    : (state.guesses === 1 ? t('1 guess') : t('{n} guesses', { n: state.guesses }))}
                  {t(' · closest first')}
                </p>
              </>
            )}

            <div className="space-y-1.5 mt-2">
              {state.list.map(g => (
                <div key={g.guess}
                     className="card flex items-center gap-3 border border-brand-border px-4 py-2.5">
                  <span className="flex-1 min-w-0 truncate text-sm font-semibold text-brand-text">
                    {g.guess}
                    {g.isHint && (
                      <span className="ml-2 text-[10px] font-semibold uppercase tracking-widest text-brand-muted">
                        {t('hint')}
                      </span>
                    )}
                    {coopNames?.get(g.guess) && (
                      <span className="ml-2 text-[10px] font-semibold uppercase tracking-widest text-brand-muted">
                        {coopNames.get(g.guess)}
                      </span>
                    )}
                  </span>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${warmth(g.rank).className}`}>
                    {warmth(g.rank).label === 'cold' ? t('cold') : warmth(g.rank).label}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {phase === 'results' && state && (
          <>
            <div className="card p-4 mt-1 rounded-2xl text-center border-2 bg-game-closeenough-light"
                 style={{ borderColor: BORDER }}>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-muted">
                {players > 1 ? t('Final standings') : t('Final results')}
              </p>
              <p className="font-display font-bold text-3xl uppercase tracking-wide mt-1"
                 style={{ color: ACCENT }}>
                {state.secret}
              </p>
              <div className="mt-3 space-y-1.5">
                {[...state.scores]
                  .sort((a, b) => (b.score ?? -1) - (a.score ?? -1))
                  .map((s, i) => (
                    <div key={s.player}
                         className="card flex items-center gap-3 border border-brand-border px-3 py-2">
                      <span className="w-7 text-center font-display font-bold"
                            style={{ color: i === 0 ? ACCENT : '#6B7280' }}>
                        {i + 1}
                      </span>
                      <span className="flex-1 text-left text-sm font-semibold text-brand-text">
                        {seatName(s.player)}
                      </span>
                      <span className="text-xs text-brand-muted">
                        {s.guesses === 1 ? t('1 guess') : t('{n} guesses', { n: s.guesses })}
                      </span>
                      <span className="font-display font-bold text-brand-text">
                        {s.score ?? '—'}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
            <EndlessOutro glassClass={GLASS} accent={ACCENT}
                          onPlayAgain={playAgain} onBackToGames={onBack} />
          </>
        )}
      </div>

      {showHowTo && (
        <HowToOverlay title={TITLE} lines={HOW_TO.map(l => t(l))} accent={ACCENT}
                      image="/games/close-enough.webp" onClose={() => setShowHowTo(false)} />
      )}
    </div>
  )
}
